// app/src/components/ui.jsx — the shared studio primitives used across screens (D2a):
// the step indicator, the aspect-aware video stage, and the gradient avatar. Every user-visible
// string routes through t() (D1-DESIGN §8.3). Icons are Lucide.

import React from 'react';
import { Check } from 'lucide-react';
import { useI18n } from '../i18n/index.jsx';
import { frameDims } from '../lib/aspect.js';

// ---- StepIndicator: 1 Idea › 2 Script › 3 Video (handoff §3/§4/§5) --------------------------
// current ∈ 'ideas' | 'script' | 'video'; steps before current render DONE (mint check), the
// current renders ACTIVE (coral), later steps PENDING (fill-track).
const STEPS = [
  { key: 'ideas', num: 1, tKey: 'step.idea' },
  { key: 'script', num: 2, tKey: 'step.script' },
  { key: 'video', num: 3, tKey: 'step.video' },
];

export function StepIndicator({ current, size = 'lg' }) {
  const { t } = useI18n();
  const idx = STEPS.findIndex((s) => s.key === current);
  const dim = size === 'sm' ? 20 : 24;
  const labelFs = size === 'sm' ? 12 : 13.5;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: size === 'sm' ? 6 : 11 }}>
      {STEPS.map((s, i) => {
        const state = i < idx ? 'done' : i === idx ? 'active' : 'pending';
        return (
          <React.Fragment key={s.key}>
            {i > 0 && <span style={{ color: 'var(--chevron)' }}>›</span>}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{
                width: dim, height: dim, borderRadius: 'var(--r-pill)', flex: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: dim * 0.5,
                background: state === 'done' ? 'var(--mint)' : state === 'active' ? 'var(--coral)' : 'var(--fill-track)',
                color: state === 'pending' ? 'var(--step-off)' : '#fff',
              }}>
                {state === 'done' ? <Check size={dim * 0.5} strokeWidth={3.2} /> : s.num}
              </span>
              <span style={{
                fontWeight: state === 'active' ? 700 : 600, fontSize: labelFs,
                color: state === 'active' ? 'var(--ink)' : 'var(--step-off)',
              }}>{t(s.tKey)}</span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ---- VideoStage: the dark letterbox that centres a frame at the chosen aspect ---------------
// (handoff §5 Generating + §6 Result). children render INSIDE the frame (brand chip, caption,
// play button, scrubber). `poster` is a gradient CSS var; the frame letterboxes to the aspect.
export function VideoStage({ aspect, mobile = false, poster = 'var(--poster-mint)', frameRadius = 14, children, stageStyle = {}, shadow = false, frameShadow = true, label = null }) {
  const { w, h } = frameDims(aspect, mobile);
  return (
    <div style={{
      inlineSize: mobile ? '100%' : 460, maxInlineSize: '100%', flex: 'none',
      blockSize: mobile ? 340 : 470, borderRadius: 'var(--r-xl)', background: 'var(--letterbox)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
      position: 'relative', boxShadow: shadow ? 'var(--shadow-stage)' : 'none', ...stageStyle,
    }}>
      <div style={{
        width: w, height: h, maxWidth: '100%', borderRadius: frameRadius, background: poster,
        position: 'relative', overflow: 'hidden', boxShadow: frameShadow ? '0 12px 30px rgba(0,0,0,.4)' : 'none',
      }}>{children}</div>
      {label && <span style={{ position: 'absolute', bottom: 10, insetInlineEnd: 13, font: '600 10px var(--font-mono)', color: 'rgba(255,255,255,.5)' }}>{label}</span>}
    </div>
  );
}

// ---- Avatar: gradient tile/circle with a letter (brands, users, series) ---------------------
export function Avatar({ letter, gradient = 'var(--poster-mint-flat)', size = 46, radius = 14, round = false, fontSize }) {
  return (
    <div style={{
      width: size, height: size, flex: 'none', borderRadius: round ? 'var(--r-pill)' : radius,
      background: gradient, display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontSize: fontSize || Math.round(size * 0.4), color: '#fff',
    }}>{letter}</div>
  );
}
